export interface HrPayrollDashboard {
  owningCompanyId?: number;
  activePayElements?: number;
  activeCompensationPackages?: number;
  openPeriods?: number;
  draftRuns?: number;
  openExceptions?: number;
  generatedPayslips?: number;
  periods?: HrPayrollPeriod[];
  runs?: HrPayrollRun[];
  exceptions?: HrPayrollException[];
}

export interface HrPayElement {
  payElementId?: number;
  owningCompanyId?: number;
  elementCode?: string;
  elementName?: string;
  elementType?: string;
  calculationType?: string;
  reportingCategory?: string;
  taxable?: boolean;
  affectsGross?: boolean;
  affectsNet?: boolean;
  defaultAmount?: number;
  defaultCurrencyCode?: string;
  active?: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrCompensationPackage {
  compensationPackageId?: number;
  owningCompanyId?: number;
  employeeId?: number;
  employeeNumber?: string;
  employeeName?: string;
  packageCode?: string;
  packageName?: string;
  packageStatus?: string;
  currencyCode?: string;
  effectiveFrom?: string;
  effectiveUntil?: string;
  payrollHold?: boolean;
  bankAccountMissing?: boolean;
  notes?: string;
  approvedByCustomerId?: number;
  approvedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  elements?: HrCompensationPackageElement[];
}

export interface HrCompensationPackageElement {
  compensationPackageElementId?: number;
  owningCompanyId?: number;
  compensationPackageId?: number;
  payElementId?: number;
  elementCode?: string;
  elementName?: string;
  elementType?: string;
  amount?: number;
  quantity?: number;
  frequency?: string;
  active?: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrPayrollPeriod {
  payrollPeriodId?: number;
  owningCompanyId?: number;
  periodCode?: string;
  periodName?: string;
  periodStatus?: string;
  periodStart?: string;
  periodEnd?: string;
  paymentDate?: string;
  currencyCode?: string;
  lockedByCustomerId?: number;
  lockedAt?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrPayrollRun {
  payrollRunId?: number;
  owningCompanyId?: number;
  payrollPeriodId?: number;
  periodCode?: string;
  periodName?: string;
  runCode?: string;
  runName?: string;
  runStatus?: string;
  calculationStartedAt?: string;
  calculationCompletedAt?: string;
  calculatedByCustomerId?: number;
  approvedByCustomerId?: number;
  approvedAt?: string;
  grossAmount?: number;
  deductionAmount?: number;
  employerContributionAmount?: number;
  netAmount?: number;
  employeeCount?: number;
  exceptionCount?: number;
  currencyCode?: string;
  accountingBatchId?: number;
  journalEntryId?: number;
  billId?: number;
  paymentProposalId?: number;
  accountingPostStatus?: string;
  paymentStatus?: string;
  bankReconciliationStatus?: string;
  reversedAt?: string;
  reversedByCustomerId?: number;
  reversalReason?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  lines?: HrPayrollRunLine[];
  exceptions?: HrPayrollException[];
}

export interface HrPayrollRunLine {
  payrollRunLineId?: number;
  owningCompanyId?: number;
  payrollRunId?: number;
  employeeId?: number;
  employeeNumber?: string;
  employeeName?: string;
  compensationPackageId?: number;
  lineStatus?: string;
  grossAmount?: number;
  deductionAmount?: number;
  employerContributionAmount?: number;
  netAmount?: number;
  exceptionCount?: number;
  currencyCode?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  elements?: HrPayrollLineElement[];
}

export interface HrPayrollLineElement {
  payrollLineElementId?: number;
  owningCompanyId?: number;
  payrollRunLineId?: number;
  payElementId?: number;
  elementCode?: string;
  elementName?: string;
  elementType?: string;
  sourceType?: string;
  sourceId?: number;
  quantity?: number;
  rate?: number;
  amount?: number;
  currencyCode?: string;
  taxable?: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrPayrollInput {
  payrollInputId?: number;
  owningCompanyId?: number;
  employeeId?: number;
  employeeNumber?: string;
  employeeName?: string;
  sourceType?: string;
  sourceId?: number;
  inputCode?: string;
  inputStatus?: string;
  payElementId?: number;
  elementCode?: string;
  elementName?: string;
  elementType?: string;
  amount?: number;
  quantity?: number;
  currencyCode?: string;
  periodStart?: string;
  periodEnd?: string;
  description?: string;
  duplicateKey?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrPayrollException {
  payrollExceptionId?: number;
  owningCompanyId?: number;
  payrollRunId?: number;
  payrollRunLineId?: number;
  employeeId?: number;
  employeeNumber?: string;
  employeeName?: string;
  exceptionType?: string;
  severity?: string;
  exceptionStatus?: string;
  description?: string;
  createdAt?: string;
  resolvedAt?: string;
  resolvedByCustomerId?: number;
  resolutionNotes?: string;
}

export interface HrPayslip {
  payslipId?: number;
  owningCompanyId?: number;
  payrollRunId?: number;
  payrollRunLineId?: number;
  employeeId?: number;
  employeeNumber?: string;
  employeeName?: string;
  payslipStatus?: string;
  payslipNumber?: string;
  grossAmount?: number;
  deductionAmount?: number;
  employerContributionAmount?: number;
  netAmount?: number;
  currencyCode?: string;
  payslipDataJson?: string;
  generatedAt?: string;
  publishedAt?: string;
  downloadedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrPayrollRunCalculationRequest {
  owningCompanyId?: number;
  payrollPeriodId?: number;
  payrollRunId?: number;
  employeeId?: number;
  runCode?: string;
  runName?: string;
  regeneratePayslips?: boolean;
}

export interface HrPayrollAccountingMapping {
  payrollAccountingMappingId?: number;
  owningCompanyId?: number;
  payElementId?: number;
  elementCode?: string;
  elementName?: string;
  elementType?: string;
  departmentId?: number;
  departmentName?: string;
  costCenterId?: number;
  employeeType?: string;
  workerCategory?: string;
  debitAccountId?: number;
  debitAccountCode?: string;
  debitAccountName?: string;
  creditAccountId?: number;
  creditAccountCode?: string;
  creditAccountName?: string;
  payableType?: string;
  mappingPriority?: number;
  active?: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HrPayrollAccountingPreview {
  owningCompanyId?: number;
  payrollRunId?: number;
  runCode?: string;
  runStatus?: string;
  accountingPostStatus?: string;
  accountingPeriodLocked?: boolean;
  totalDebits?: number;
  totalCredits?: number;
  outOfBalanceAmount?: number;
  lineCount?: number;
  unmappedLineCount?: number;
  currencyCode?: string;
  lines?: HrPayrollAccountingLine[];
}

export interface HrPayrollAccountingBatch {
  payrollAccountingBatchId?: number;
  owningCompanyId?: number;
  payrollRunId?: number;
  batchCode?: string;
  batchStatus?: string;
  journalEntryId?: number;
  billId?: number;
  paymentProposalId?: number;
  grossAmount?: number;
  deductionAmount?: number;
  employerContributionAmount?: number;
  netAmount?: number;
  currencyCode?: string;
  postingDate?: string;
  postedAt?: string;
  postedByCustomerId?: number;
  reversedAt?: string;
  reversedByCustomerId?: number;
  reversalReason?: string;
  paymentStatus?: string;
  bankReconciliationStatus?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  lines?: HrPayrollAccountingLine[];
}

export interface HrPayrollAccountingLine {
  payrollAccountingBatchLineId?: number;
  owningCompanyId?: number;
  payrollAccountingBatchId?: number;
  payrollRunId?: number;
  payrollRunLineId?: number;
  payrollLineElementId?: number;
  employeeId?: number;
  employeeNumber?: string;
  employeeName?: string;
  payElementId?: number;
  elementCode?: string;
  elementName?: string;
  elementType?: string;
  lineCategory?: string;
  debitCredit?: string;
  accountId?: number;
  accountCode?: string;
  accountName?: string;
  amount?: number;
  currencyCode?: string;
  departmentId?: number;
  costCenterId?: number;
  mappingStatus?: string;
  mappingMessage?: string;
  createdAt?: string;
}

export interface HrPayrollPaymentBatch {
  payrollPaymentBatchId?: number;
  owningCompanyId?: number;
  payrollRunId?: number;
  payrollAccountingBatchId?: number;
  paymentBatchCode?: string;
  paymentBatchStatus?: string;
  paymentProposalId?: number;
  bankAccountId?: number;
  currencyCode?: string;
  totalNetAmount?: number;
  lineCount?: number;
  proposedAt?: string;
  proposedByCustomerId?: number;
  approvedAt?: string;
  approvedByCustomerId?: number;
  cancelledAt?: string;
  cancelledByCustomerId?: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  lines?: HrPayrollPaymentLine[];
}

export interface HrPayrollPaymentLine {
  payrollPaymentLineId?: number;
  owningCompanyId?: number;
  payrollPaymentBatchId?: number;
  payrollRunId?: number;
  payrollRunLineId?: number;
  employeeId?: number;
  employeeNumber?: string;
  employeeName?: string;
  netAmount?: number;
  cashPaidAmount?: number;
  remainingDue?: number;
  currencyCode?: string;
  paymentLineStatus?: string;
  bankAccountId?: number;
  paymentReference?: string;
  reconciliationStatus?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const HR_PAY_ELEMENT_TYPES = ['EARNING', 'DEDUCTION', 'EMPLOYER_CONTRIBUTION', 'INFORMATION'];

export const HR_PAY_CALCULATION_TYPES = ['FIXED', 'QUANTITY_RATE', 'PERCENTAGE'];

export const HR_COMPENSATION_PACKAGE_STATUSES = ['DRAFT', 'APPROVED', 'ARCHIVED'];

export const HR_PAYROLL_PERIOD_STATUSES = ['OPEN', 'LOCKED', 'CLOSED'];

export const HR_PAYROLL_RUN_STATUSES = ['DRAFT', 'CALCULATING', 'CALCULATED', 'CALCULATED_WITH_EXCEPTIONS', 'APPROVED'];

export const HR_PAYROLL_INPUT_STATUSES = ['READY', 'HELD', 'CONSUMED', 'CANCELLED'];
